import Big from 'big.js';

import calculateCarbonData from 'src/lib/carbondata/calculator';
import {
  BUY, BUYER, DIRECTIONS,
  METER, SELLER, TIME_ZONE_SYSTEM,
  TRADE, TRADE_DIRECTIONS_LABEL,
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
  TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { getCounterFactualValue } from 'src/util/counterfactual';
import {
  extractData, filterRulesByTradePointId,
  getMemberNodes, getTimeStamp, getUntradedVolume, hasCounterfactual,
} from 'src/util/mainDataBuilder';
import { isNumber } from 'src/util/math';

/**
 * Prepares a map of property members
 * @param {object} propertyMembers
 * @returns {object} - map of property members.
 */
export const getPropertyMembers = (propertyMembers) => {
  const members = {};
  const properties = {};
  if (!propertyMembers) {
    return { members, properties };
  }
  propertyMembers?.forEach((member) => {
    const { uuid } = member || {};
    members[uuid] = { ...member, propertyId: member.property?.uuid };
    // eslint-disable-next-line no-unused-vars
    const { property, ...remainingAttrs } = members[uuid] || {};
    if (members[uuid]) members[uuid] = remainingAttrs;
    if (property?.uuid) properties[property?.uuid] = property;
  });
  return { members, properties };
};

/**
 * Prepares a map of portfolio user members
 * @param {object} userMembers
 * @returns {object} - map of portfolio user members.
 */
export const getUserMembers = (userMembers) => {
  const members = {};
  const users = {};
  if (!userMembers) {
    return { members, users };
  }
  const memberNodes = userMembers.edges?.map((item) => (
    { ...item.node, userId: item.node.user?.uuid }));

  memberNodes?.forEach((member) => {
    const { uuid } = member || {};
    // eslint-disable-next-line no-unused-vars
    const { user, ...remainingAttrs } = member;
    if (uuid) members[uuid] = remainingAttrs;
    if (user?.uuid) users[user.uuid] = user;
  });
  return { members, users };
};

/**
 * Build main data that is to be used in portfolio dashboard
 * @param {object} portfolio The portfolio of properties.
 * @param {string} timezone The timezone of the portfolio.
 * @returns {object} - main data
 */
export const buildMainData = (portfolio, timezone) => {
  const {
    active, externalIdentifier, uuid, title,
    meters: metersConnection,
    tradeSetSummary,
    propertyMembers: propertyMembersConnection,
    userMembers: userMembersConnection,
  } = portfolio || {};

  const extractList = [];
  const meterNodes = getMemberNodes(metersConnection) || [];
  meterNodes.forEach((meterNode) => {
    const { tradePointId } = meterNode || {};
    if (!tradePointId) return;
    const filteredTradeSummaries = tradeSetSummary?.filter((tradeSet) => (
      tradeSet?.data?.some(({
        buyerTradePointIds = [], directions = [], sellerTradePointIds = [],
      }) => {
        if (directions.length !== 1) return false;
        const direction = directions[0];

        if (direction === TRADE_DIRECTION_BUY) {
          return buyerTradePointIds.includes(tradePointId);
        }
        if (direction === TRADE_DIRECTION_SELL) {
          return sellerTradePointIds.includes(tradePointId);
        }
        return false;
      })
    ));
    extractList.push(extractData({ ...meterNode, ...{ tradeSetSummary: filteredTradeSummaries } }));
  });

  const { members: userMembers, users } = getUserMembers(userMembersConnection);
  const propertyNodes = getMemberNodes(propertyMembersConnection) || [];
  const { members: propertyMembers, properties } = getPropertyMembers(propertyNodes);

  const mainData = {
    portfolio: {
      active,
      externalIdentifier,
      uuid,
      propertyMembers,
      title,
      userMembers,
    },
    buy: { rules: {}, data: {} },
    sell: { rules: {}, data: {} },
    meters: {},
    properties: {},
    users,
  };
  const buyTimestamps = [];
  const sellTimestamps = [];

  // form rules and extract timestamp list
  if (extractList.length > 0) {
    extractList.forEach((data) => {
      const {
        rules, tradePointId, buy, sell, meters,
      } = data;
      // eslint-disable-next-line no-unused-vars
      const { property, ...meterData } = meters || {};
      const { meter: buyMeter } = buy;
      const { meter: sellMeter } = sell;
      const propertyId = property?.uuid;
      const currentProperty = properties[propertyId];
      const buyTimestampList = buyMeter?.data?.map((datum) => datum?.timestamp) || [];
      const sellTimestampList = sellMeter?.data?.map((datum) => datum?.timestamp) || [];
      buyTimestamps.push(...buyTimestampList);
      sellTimestamps.push(...sellTimestampList);
      mainData.buy.rules = {
        ...mainData.buy.rules,
        ...filterRulesByTradePointId(rules, tradePointId, BUYER),
      };
      mainData.sell.rules = {
        ...mainData.sell.rules,
        ...filterRulesByTradePointId(rules, tradePointId, SELLER),
      };
      mainData.meters = {
        ...mainData.meters, [meters.uuid]: { ...meterData, propertyId },
      };
      mainData.properties = { ...mainData.properties, [propertyId]: currentProperty };
    });
  }

  // timestamps to form the object keys
  const timestamps = {
    buy: [...new Set(buyTimestamps)],
    sell: [...new Set(sellTimestamps)],
  };

  // looping over all the timestamps
  DIRECTIONS.forEach((direction) => {
    timestamps[direction].forEach((timestamp) => {
      extractList.forEach((datum) => {
        const {
          uuid: meterId, propertyId,
          tradePointId,
        } = datum?.meters || {};
        const propertyRegion = mainData.properties[propertyId]?.publicHolidayRegion;
        const { trades, meter } = datum[direction] || {};
        const { data: meterData, aggregation } = meter || {};

        if (!aggregation) return mainData;

        const x = getTimeStamp(aggregation, timestamp, timezone);
        const finalTimestamp = x.ts;

        // assigning default values
        const {
          meterDataAggregates: defaultMeter = {},
          tradeSetSummaries: defaultTrades = {},
          untradedDataAggregates: defaultUntraded = {},
        } = mainData[direction].data[finalTimestamp] || {};

        mainData[direction].data[finalTimestamp] = {
          meterDataAggregates: defaultMeter,
          tradeSetSummaries: defaultTrades,
          untradedDataAggregates: defaultUntraded,
        };
        // Add tradeset summary grouped by rule id
        if (trades && trades.length > 0) {
          trades.forEach((trade) => {
            const { key, data: tradeData } = trade || {};

            const { ruleId } = key || {};
            if (ruleId && tradeData) {
              const filteredData = tradeData.filter((d) => d?.range?.finish === timestamp);
              let tradeDatum = {
                value: 0, volume: 0, carbon: 0, counterfactual: NaN, meterId,
              };
              let filteredDatum = filteredData && filteredData[0] ? filteredData[0] : '';
              if (filteredDatum) {
                const { directions: tradeDirections, types: tradeTypes, volume } = filteredDatum;
                const tradeDirection = tradeDirections?.length === 1 ? tradeDirections[0] : '';
                const type = tradeTypes?.length === 1 ? tradeTypes[0] : '';

                filteredDatum = {
                  ...filteredDatum,
                  direction: tradeDirection,
                  type,
                  meterId,
                  tradePointId,
                };
                let carbon = 0;
                if (direction === BUY) {
                  const carbonDataProps = {
                    dataType: TRADE,
                    timestamp: x,
                    energy: isNumber(volume) ? Big(volume) : Big(0),
                    propertyRegion,
                    direction: tradeDirection,
                    type,
                  };
                  carbon = calculateCarbonData(carbonDataProps);
                }
                const filteredRules = mainData[direction].rules;

                const tradeSummaryInput = {
                  direction: TRADE_DIRECTIONS_LABEL[direction],
                  range: filteredDatum.range,
                  tradePointId,
                  volume,
                };
                const counterfactual = type === TRADE_TYPE_RESIDUAL
                  ? NaN : getCounterFactualValue(tradeSummaryInput, Object.values(filteredRules));

                filteredDatum = {
                  ...filteredDatum,
                  carbon: Number(carbon),
                  counterfactual,
                };

                // eslint-disable-next-line no-unused-vars
                const { directions, types, ...finalDatum } = filteredDatum; // remove unused keys
                tradeDatum = finalDatum;
              }
              mainData[direction].data[finalTimestamp].tradeSetSummaries[ruleId] = tradeDatum;
            }
          });
        }

        // add meter data grouped by meter trade point id
        if (meterData && meterId) {
          const filteredData = meterData.filter((d) => d?.timestamp === timestamp) || {};
          let meterDatum = filteredData && filteredData[0] ? filteredData[0] : {};
          const { value } = meterDatum;
          let carbon = 0;

          if (direction === BUY) {
            const carbonDataProps = {
              dataType: METER,
              timestamp: x,
              energy: isNumber(value) ? Big(value) : Big(0),
              propertyRegion,
              direction: TRADE_DIRECTIONS_LABEL[direction],
            };
            carbon = calculateCarbonData(carbonDataProps);
          }
          meterDatum = {
            ...meterDatum,
            carbon: Number(carbon),
          };
          mainData[direction].data[finalTimestamp].meterDataAggregates[meterId] = meterDatum;
        }

        return mainData;
      });
    });

    // Add untraded energy
    const tempData = { ...mainData };
    Object.entries(tempData[direction].data)?.forEach(([timestamp, info]) => {
      const { meterDataAggregates, tradeSetSummaries } = info;
      const energy = Big(0);
      let energyByTradePointId = {};
      const untradedDataBymeter = {};

      Object.keys(meterDataAggregates)?.forEach((meterId) => {
        const meter = meterDataAggregates[meterId];
        if (!meter) return;
        const { value } = meter;
        energyByTradePointId = isNumber(value)
          ? { ...energyByTradePointId, [meterId]: energy.plus(value) }
          : { ...energyByTradePointId, [meterId]: energy };
        untradedDataBymeter[meterId] = { volume: energyByTradePointId[meterId] };
      });

      Object.keys(tradeSetSummaries)?.forEach((tradeId) => {
        if (tradeSetSummaries[tradeId]) {
          const { volume, meterId } = tradeSetSummaries[tradeId];
          const meterData = mainData.meters[meterId];
          if (meterData?.aggregation && meterDataAggregates[meterId]) {
            const tradedEnergyVolume = isNumber(volume) ? Big(volume) : Big(0);
            // Cannot assume untraded data exists for all meters
            const untradedEnergyVolume = untradedDataBymeter[meterId]?.volume;
            if (untradedEnergyVolume > 0) {
              untradedDataBymeter[meterId].volume = getUntradedVolume(
                untradedEnergyVolume,
                tradedEnergyVolume,
              );
            }
          }
        }
      });

      const allRules = mainData[direction].rules;
      const directionLabel = TRADE_DIRECTIONS_LABEL[direction];
      Object.keys(untradedDataBymeter)?.forEach((meterId) => {
        // Clamp unconditionally: the metered aggregate itself (not just the traded
        // subtraction above) can be a small negative due to floating-point noise.
        const untradedVolume = getUntradedVolume(untradedDataBymeter[meterId].volume);
        const filteredMeter = mainData.meters[meterId];
        const { propertyId, tradePointId } = filteredMeter || {};
        const region = mainData.properties[propertyId]?.publicHolidayRegion || null;
        const carbonDataProps = {
          dataType: TRADE,
          timestamp,
          energy: untradedVolume,
          direction: directionLabel,
          type: TRADE_TYPE_RESIDUAL,
          propertyRegion: region,
        };
        const untradedValue = getCounterFactualValue({
          direction: directionLabel,
          range: { start: timestamp, finish: timestamp },
          tradePointId,
          volume: untradedVolume,
        }, Object.values(allRules));

        const carbonData = calculateCarbonData(carbonDataProps);
        untradedDataBymeter[meterId] = {
          ...untradedDataBymeter[meterId],
          volume: untradedVolume,
          carbon: carbonData,
          value: untradedValue,
        };
      });
      mainData[direction].data[timestamp].untradedDataAggregates = untradedDataBymeter;
    });
  });

  mainData.hasCounterfactual = hasCounterfactual(mainData);
  mainData.timezone = timezone || TIME_ZONE_SYSTEM;
  return mainData;
};
